import axios from 'axios'

import {API_BASE, apiErrorHandler, ApiFilter, isDefinedAndNotNull, isNotEmpty} from '../api'
import {objectToSnakeCase} from '../utils'
import Auth from '../auth'

const STIPEND_REQUEST_ENDPOINT = `${API_BASE}stipend_requests/`
const ADMIN_STIPEND_REQUEST_ENDPOINT = `${API_BASE}admin/stipend_requests/`

export default class StipendRequestAPI {
  constructor(cls) {
    this.cls = cls
  }

  static create(cls) {
    return new StipendRequestAPI(cls)
  }

  create(stipend_request) {
    const data = this.cls.toAPI(stipend_request)
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
    }
    return axios
      .post(STIPEND_REQUEST_ENDPOINT, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({apiName: 'Start StipendRequest API error'}))
  }

  list({pagination, filters}) {
    const filtersMap = {
      // Pagination
      page: ApiFilter.create({key: 'page'}),
      pageSize: ApiFilter.create({key: 'page_size'}),

      // Sorting
      ordering: ApiFilter.create({key: 'ordering'}),

      // Filtering
      statuses: ApiFilter.create({
        key: 'status',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(','),
      }),
      organizations: ApiFilter.create({
        key: 'organization',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(','),
      }),
      subOrganizations: ApiFilter.create({
        key: 'sub_organization',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(','),
      }),
      referredOnly: ApiFilter.create({key: 'referred_only'}),
      nonReferredOnly: ApiFilter.create({key: 'non_referred_only'}),
      submittedStatuses: ApiFilter.create({
        key: 'submitted_status',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(','),
      }),
      reportableEventCategories: ApiFilter.create({
        key: 'reportable_event_categories',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(','),
      }),
      eventCategories: ApiFilter.create({
        key: 'event_categories',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(','),
      }),
      programMonths: ApiFilter.create({
        key: 'program_month',
        validators: [isDefinedAndNotNull, isNotEmpty],
        extractor: l => l.join(',')
      }),

      // employee
      user: ApiFilter.create({key: 'user'}),

      // Staff member assigned
      assignedTo: ApiFilter.create({key: 'assigned_to'}),
    }
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
      params: ApiFilter.buildParams(filtersMap, {...pagination, ...filters}),
    }

    const promise = axios
      .get(`${STIPEND_REQUEST_ENDPOINT}`, options)
      .then(response => response.data)
      .then(data => {
        return {
          ...data,
          results: data.results.map(this.cls.fromAPI),
        }
      })
      .catch(
        apiErrorHandler({
          apiName: 'List Stipendstipend_stipend_requests API error',
          rethrowErrors: true,
        }),
      )
    return promise
  }

  retrieve(stipendRequestId) {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
    }

    const promise = axios
      .get(`${STIPEND_REQUEST_ENDPOINT}${stipendRequestId}/`, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({apiName: 'Retrieve Stipendstipend_request API error'}))
    return promise
  }

  async update(stipend_request = null, fields = [], excludeFields = [], kwargs = null) {
    const url = `${STIPEND_REQUEST_ENDPOINT}${(stipend_request && stipend_request.id) ||
      kwargs.id}/`
    let data = this.cls.toAPI(stipend_request, fields, excludeFields, kwargs)
    const options = {
      headers: Auth.getHeaders(),
    }
    return axios
      .patch(url, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(
        apiErrorHandler({
          apiName: 'Update Stipend Requst API error',
          rethrowErrors: true,
        }),
      )
  }

  /**
   * Keep alive ping to set editing timestamp of stipend_request
   */
  editingPing(stipend_request) {
    const url = `${STIPEND_REQUEST_ENDPOINT}${stipend_request.id}/editing/`
    const options = {
      headers: Auth.getHeaders(),
    }
    return axios.post(url, {}, options)
  }

  /**
   * Update status of a stipend_request
   */
  async updateStatus(stipend_request) {
    const url = `${STIPEND_REQUEST_ENDPOINT}${stipend_request.id}/update-status/`

    // Create the data structure expected by the API
    const data = {
      status: stipend_request.status,
    }
    if (stipend_request.status === this.cls.options.DECLINED) {
      data.declined_reason = stipend_request.declinedReason
      data.declined_reason_detail = stipend_request.declinedReasonDetail
    }
    if (
      stipend_request.status === this.cls.options.APPROVED ||
      stipend_request.status === this.cls.options.PARTIALLY_APPROVED ||
      stipend_request.status === this.cls.options.PARTIALLY_APPROVED_PENDING
    ) {
      data.amount = stipend_request.amount
    }

    if (stipend_request.payoutType) {
      data.payout_type = stipend_request.payoutType
    }

    if (stipend_request.isAtRisk) {
      data.is_at_risk = true
    }

    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
    }
    const promise = axios
      .post(url, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(
        apiErrorHandler({
          apiName: 'Update Status Stipendstipend_request API error',
          rethrowErrors: true,
        }),
      )
    return promise
  }

  /**
   * Refer a stipend_request to an admin at an organization
   */
  async refer(stipend_request) {
    const url = `${STIPEND_REQUEST_ENDPOINT}${stipend_request.id}/refer-to-organization/`
    const options = {
      headers: {
        ...Auth.getHeaders(),
      },
    }
    const promise = axios
      .post(url, {}, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(
        apiErrorHandler({
          apiName: 'Refer Stipendstipend_request API error',
          rethrowErrors: true,
        }),
      )
    return promise
  }

  /**
   * Logs an admin viewing stipend_request details
   */
  async logAdminView(stipend_request) {
    const url = `${STIPEND_REQUEST_ENDPOINT}${stipend_request.id}/log-view/`
    const options = {
      headers: {
        ...Auth.getHeaders(),
      },
    }
    const promise = axios
      .post(url, {}, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(
        apiErrorHandler({
          apiName: 'Log Stipendstipend_request View API error',
          rethrowErrors: true,
        }),
      )
    return promise
  }

  async adminPartialUpdate(stipend_request, fields) {
    const url = `${ADMIN_STIPEND_REQUEST_ENDPOINT}${stipend_request.id}/`
    const options = {
      headers: {
        ...Auth.getHeaders(),
      },
    }

    const data = this.cls.toAPI(stipend_request, fields)
    const promise = axios
      .patch(url, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(
        apiErrorHandler({
          apiName: 'Admin Partial Update API error',
          rethrowErrors: true,
        }),
      )
    return promise
  }
}
